import { useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";

const SITE_URL = "https://www.bicenter-ai.com";

const META = {
  fr: {
    title: "BICENTER AI Business Suite — L'écosystème IA business premium",
    description: "BICENTER AI Business Suite — l'écosystème intelligent d'IA pour la performance business. BICENTER AI commercialise ses solutions numériques d'IA (Business Model, Automatisation, RH, Consulting, eV-Card, Speaker) pour startups, fondateurs, incubateurs et investisseurs.",
    keywords: "BICENTER AI, IA business, intelligence artificielle entreprise, solution IA startup, automatisation IA, business model IA, SaaS IA, IA pour fondateurs, IA pour décideurs, mise en relation IA candidats professionnels, marketplace talents IA, conseil stratégique IA, plateforme IA Europe, IA Afrique, accélérateur IA, investisseurs IA, conférence IA business, webinaire IA, masterclass IA, AI business operating system, BICENTER AI Business, BICENTER AI Automation, BICENTER AI Consulting, BICENTER AI RH, BICENTER AI eV-Card, BICENTER AI Speaker, carte de visite intelligente, plateforme speaker",
    ogTitle: "BICENTER AI Business Suite — L'écosystème intelligent d'IA pour la performance business",
    ogDescription: "Structurez, automatisez et scalez votre business avec BICENTER AI. Solutions IA pour startups, fondateurs, décideurs et investisseurs. Démos hebdomadaires, conférences AI Business & Tech.",
    ogLocale: "fr_FR",
  },
  en: {
    title: "BICENTER AI Business Suite — The premium AI business ecosystem",
    description: "BICENTER AI Business Suite — the intelligent AI ecosystem for business performance. BICENTER AI commercializes intelligent digital solutions (Business Model, Automation, HR, Consulting, eV-Card, Speaker) for startups, founders, incubators and investors.",
    keywords: "BICENTER AI, AI for business, artificial intelligence enterprise, AI startup solution, AI automation, AI business model, AI SaaS, AI for founders, AI for decision-makers, AI candidate-professional matching, AI talent marketplace, AI strategic advisory, AI platform Europe, AI Africa, AI accelerator, AI investors, AI business conference, AI webinar, AI masterclass, AI business operating system, BICENTER AI Business, BICENTER AI Automation, BICENTER AI Consulting, BICENTER AI RH, BICENTER AI eV-Card, BICENTER AI Speaker, smart business card, speaker platform",
    ogTitle: "BICENTER AI Business Suite — The intelligent AI ecosystem for business performance",
    ogDescription: "Structure, automate and scale your business with BICENTER AI. AI solutions for startups, founders, decision-makers and investors. Weekly demos, AI Business & Tech conferences.",
    ogLocale: "en_US",
  },
};

const upsertMeta = (attr, key, content) => {
  if (content === undefined || content === null) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, hreflang, href) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const SEO = () => {
  const { lang } = useLanguage();

  useEffect(() => {
    const m = META[lang] || META.fr;
    const cleanTitle = m.title;

    // Title + core meta
    document.title = cleanTitle;
    document.documentElement.lang = lang;
    upsertMeta("name", "description", m.description);
    upsertMeta("name", "keywords", m.keywords);

    // Open Graph
    upsertMeta("property", "og:title", m.ogTitle);
    upsertMeta("property", "og:description", m.ogDescription);
    upsertMeta("property", "og:locale", m.ogLocale);
    upsertMeta("property", "og:url", SITE_URL + "/");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "BICENTER AI");
    upsertMeta("property", "og:image", SITE_URL + "/bicenter-ai-logo.png");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", m.ogTitle);
    upsertMeta("name", "twitter:description", m.ogDescription);
    upsertMeta("name", "twitter:image", SITE_URL + "/bicenter-ai-logo.png");

    // Canonical + hreflang
    upsertLink("canonical", null, SITE_URL + "/");
    upsertLink("alternate", "fr", SITE_URL + "/");
    upsertLink("alternate", "en", SITE_URL + "/?lang=en");
    upsertLink("alternate", "x-default", SITE_URL + "/");

    // Guard: revert any external mutation of the <title> that injects Emergent branding
    const titleEl = document.querySelector("title");
    if (!titleEl) return;
    const observer = new MutationObserver(() => {
      if (document.title !== cleanTitle && /emergent/i.test(document.title)) {
        document.title = cleanTitle;
      }
    });
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, [lang]);

  return null;
};
